/**
 * SpecLibraryModal — the firm's master spec library, as a READ-ONLY EXPLAINER (admin-gated).
 *
 * Opened via `open-spec-library` (SiteNav). It does NOT edit the library — it explains how each
 * section is constructed so you can hand Claude Code a precise reference to change it on the back end
 * (faster + more reliable than piecing the logic together in the UI). Two tabs:
 *   - Sections:      list → "how this section works": when it appears (plain English), the outline
 *                    summary + client action, and the body with every {{variable}} highlighted
 *                    (click → a popup: source / key / logic) and {{#compare}} blocks as plain-English
 *                    "shown when …" callouts. A copy-reference chip yields tokens like spec-master:07-RF.
 *   - Questionnaire: read-only reverse map — each question → key, type, options, and which sections it drives.
 *
 * Edits go through Claude Code: copy a reference here, paste it in chat. Data via services/specMaster.js
 * (read path only). Explain helpers in services/specExplain.js.
 */

import { useEffect, useState, useMemo } from 'react'
import { useAuth, useUser } from '@clerk/clerk-react'
import { useDarkMode } from '../outline/theme.js'
import { isSeatLoaded, hasModule, loadCurrentUserSeat } from '../services/tenancy.js'
import { getMasterLibrary, getMasterLines } from '../services/specMaster.js'
import { sectionReferencedKeys, computeOutlineLabels } from '../services/specEngine.js'
import { ruleToEnglish, describeVariable, parseTemplateNodes, ruleSignature, ruleToParts } from '../services/specExplain.js'

// Distinct, theme-stable hues for condition groups. A condition keeps its colour across the whole
// section view, so lines that appear together read as a cluster at a glance.
const COND_PALETTE = ['#2f7ed8', '#27ae60', '#e08e0b', '#9b59b6', '#d9534f', '#16a3a3', '#c2185b', '#6b8e23', '#0e7490', '#a16207']

function copyText(t) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(t).catch(() => {})
  try { const ta = document.createElement('textarea'); ta.value = t; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove() } catch {}
}
const SRC_COLOR = { Question: '#1D9E75', Derived: '#8a63d2', 'Project record': '#2563eb', Other: '#8a8a8a' }

export default function SpecLibraryModal() {
  const dark = useDarkMode()
  const { isSignedIn, getToken } = useAuth()
  const { user } = useUser()
  const [open, setOpen]     = useState(false)
  const [seatReady, setSeatReady] = useState(isSeatLoaded())
  const [tab, setTab]       = useState('sections')
  const [lib, setLib]       = useState(null)
  const [selId, setSelId]   = useState(null)
  const [err, setErr]       = useState(null)

  const bg       = dark ? '#0b1c24' : '#f4f7f8'
  const border   = dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.12)'
  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.50)'
  const inputBg  = dark ? 'rgba(255,255,255,0.05)' : '#f4f7f8'

  useEffect(() => {
    function onOpen() { setOpen(true) }
    window.addEventListener('open-spec-library', onOpen)
    return () => window.removeEventListener('open-spec-library', onOpen)
  }, [])
  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
  }, [open])
  useEffect(() => {
    if (!open || !user?.id) return
    window.__clerkGetToken = () => getToken()
    if (!isSeatLoaded()) loadCurrentUserSeat(user.id).then(() => setSeatReady(true))
    else setSeatReady(true)
  }, [open, user?.id, getToken])
  useEffect(() => {
    if (!open || !seatReady || !hasModule('admin') || lib) return
    getMasterLibrary().then(setLib).catch(e => setErr(e.message))
  }, [open, seatReady, lib])

  if (!open || !isSignedIn) return null

  const card = { background: bg, borderRadius: 12, border: `0.5px solid ${border}`, boxShadow: '0 8px 40px rgba(0,0,0,0.20)',
    width: '100%', maxWidth: 1100, height: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }
  const tabStyle = t => ({ padding: '8px 18px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: tab === t ? 600 : 400,
    color: tab === t ? textPrim : textMut, background: 'none', border: 'none',
    borderBottom: tab === t ? '2px solid #07272D' : '2px solid transparent', cursor: 'pointer', marginBottom: -1 })

  const sections = (lib && lib.sections) || []
  const fields   = (lib && lib.fields) || []
  const selected = sections.find(s => s.id === selId) || null
  const theme = { dark, border, textPrim, textMut, inputBg }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(7,39,45,0.48)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div data-modal-id="SpecLibraryModal" style={card}>
        <button onClick={() => setOpen(false)} title="Close (Esc)"
          style={{ position: 'absolute', top: 14, right: 16, zIndex: 10, background: 'none', border: 'none', cursor: 'pointer', color: textMut, fontSize: 22, lineHeight: 1 }}>×</button>
        <div style={{ padding: '18px 24px 0' }}>
          <div style={{ fontFamily: '"Montserrat",sans-serif', fontWeight: 700, fontSize: 15, letterSpacing: '0.06em', textTransform: 'uppercase', color: textPrim }}>AlderSPEC Library</div>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: textMut, marginTop: 3 }}>
            Reference only — see how each section is built, then ask Claude Code to change it (copy a reference below).
          </div>
          <div style={{ display: 'flex', borderBottom: `1px solid ${border}`, marginTop: 12 }}>
            <button style={tabStyle('sections')} onClick={() => setTab('sections')}>Sections ({sections.length})</button>
            <button style={tabStyle('questionnaire')} onClick={() => setTab('questionnaire')}>Questionnaire ({fields.length})</button>
          </div>
        </div>

        {!seatReady || !hasModule('admin') ? (
          <Center textMut={textMut}>{seatReady ? 'Access restricted.' : 'Loading…'}</Center>
        ) : err ? (
          <Center color="#c0392b">{err}</Center>
        ) : !lib ? (
          <Center textMut={textMut}>Loading library…</Center>
        ) : tab === 'sections' ? (
          <SectionsTab {...{ sections, fields, selected, setSelId, theme }} />
        ) : (
          <QuestionnaireTab {...{ sections, fields, theme }} />
        )}
      </div>
    </div>
  )
}

function Center({ children, textMut, color }) {
  return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: color || textMut, fontFamily: '"DM Sans",sans-serif', fontSize: 13 }}>{children}</div>
}

/* ── Sections tab ──────────────────────────────────────────────────────────── */
function SectionsTab({ sections, fields, selected, setSelId, theme }) {
  const { border, textPrim, textMut, dark } = theme
  const byDiv = {}
  for (const s of sections) { const d = s.division || '—'; (byDiv[d] = byDiv[d] || []).push(s) }
  const divs = Object.keys(byDiv).sort()
  const [collapsedDivs, setCollapsedDivs] = useState(() => new Set())
  const toggleDiv = d => setCollapsedDivs(prev => { const n = new Set(prev); n.has(d) ? n.delete(d) : n.add(d); return n })
  const selBg = dark ? 'rgba(255,255,255,0.08)' : 'rgba(7,39,45,0.07)'
  const accent = dark ? '#9ec5d4' : '#07272D'
  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      <div style={{ width: 290, borderRight: `0.5px solid ${border}`, overflowY: 'auto', padding: '4px 0 10px' }}>
        {divs.map(d => {
          const isColl = collapsedDivs.has(d)
          return (
            <div key={d}>
              {/* Division header — click to expand/collapse; clear separator between divisions */}
              <button onClick={() => toggleDiv(d)}
                style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left',
                  padding: '7px 12px 6px', marginTop: 2, cursor: 'pointer',
                  border: 'none', borderTop: `0.5px solid ${border}`,
                  background: dark ? 'rgba(255,255,255,0.035)' : 'rgba(7,39,45,0.04)',
                  fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: textMut }}>
                <span style={{ flex: '0 0 8px', fontSize: 8, transform: isColl ? 'none' : 'rotate(90deg)', transition: 'transform 0.12s' }}>▸</span>
                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Div {d} {byDiv[d][0]?.division_name || ''}</span>
                <span style={{ flex: '0 0 auto', opacity: 0.7 }}>{byDiv[d].length}</span>
              </button>
              {!isColl && byDiv[d].map(s => {
                const isSel = selected && selected.id === s.id
                return (
                  <button key={s.id} onClick={() => setSelId(s.id)} title={s.section_number + '  ' + s.section_name}
                    style={{ display: 'flex', alignItems: 'baseline', gap: 8, width: '100%', textAlign: 'left',
                      padding: '5px 14px 5px 22px', fontFamily: '"DM Sans",sans-serif', fontSize: 12,
                      background: isSel ? selBg : 'none', cursor: 'pointer', color: textPrim,
                      border: 'none', borderLeft: `2px solid ${isSel ? accent : 'transparent'}` }}>
                    <span style={{ flex: '0 0 48px', fontFamily: '"DM Mono",monospace', fontSize: 10, color: textMut }}>{s.section_number}</span>
                    <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.section_name}</span>
                    {s.expects_submittals && (
                      <span title="Requires contractor submittals during construction"
                        style={{ flex: '0 0 auto', fontFamily: '"DM Mono",monospace', fontSize: 8, letterSpacing: '0.06em',
                          color: dark ? '#e0a96d' : '#b8640a', background: dark ? 'rgba(184,100,10,0.22)' : '#fbe7d6',
                          padding: '1px 4px', borderRadius: 3 }}>SUB</span>
                    )}
                  </button>
                )
              })}
            </div>
          )
        })}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {!selected ? <div style={{ color: textMut, fontFamily: '"DM Sans",sans-serif', fontSize: 13 }}>Select a section to see how it works.</div>
          : <SectionExplainer key={selected.id} section={selected} fields={fields} theme={theme} />}
      </div>
    </div>
  )
}

function SectionExplainer({ section, fields, theme }) {
  const { border, textPrim, textMut, inputBg, dark } = theme
  const [popover, setPopover] = useState(null)   // { v, top, left }
  const [copied, setCopied] = useState(null)
  const [lines, setLines] = useState(null)        // null = loading; [] = none; [...] = outline lines
  useEffect(() => {
    let live = true
    setLines(null)
    getMasterLines(section.id).then(ls => { if (live) setLines(ls || []) }).catch(() => { if (live) setLines([]) })
    return () => { live = false }
  }, [section.id])
  const fieldsByKey = useMemo(() => { const m = {}; for (const f of fields) m[f.key] = f; return m }, [fields])
  const vtheme = useMemo(() => ({ ...theme, fieldsByKey }), [theme, fieldsByKey])
  const ref = 'spec-master:' + section.section_number
  const usedKeys = useMemo(() => [...sectionReferencedKeys(section)].sort(), [section])

  function openVar(e, key) {
    e.stopPropagation()
    const r = e.currentTarget.getBoundingClientRect()
    setPopover({ v: describeVariable(key, fieldsByKey), top: r.bottom + 4, left: Math.min(r.left, window.innerWidth - 320) })
  }
  function doCopy(t, tag) { copyText(t); setCopied(tag); setTimeout(() => setCopied(null), 1100) }

  const lbl = { fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: textMut, margin: '16px 0 5px' }
  const bodyText = { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.55, color: textPrim }
  const refChip = { fontFamily: '"DM Mono",monospace', fontSize: 10, background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(7,39,45,0.07)', color: textPrim, border: 'none', borderRadius: 4, padding: '2px 7px', cursor: 'pointer' }

  return (
    <div onClick={() => setPopover(null)}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: textMut }}>{section.section_number}</span>
        <span style={{ fontFamily: '"Montserrat",sans-serif', fontWeight: 700, fontSize: 13, letterSpacing: '0.03em', textTransform: 'uppercase', color: textPrim }}>{section.section_name}</span>
        <button style={refChip} title="Copy a reference to paste to Claude Code" onClick={() => doCopy(ref, 'ref')}>{copied === 'ref' ? '✓ copied' : 'copy ref · ' + ref}</button>
        {section.expects_submittals && (
          <span title="This section requires contractor submittals (product data, shop drawings, or samples) during construction"
            style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: dark ? '#e0a96d' : '#b8640a', background: dark ? 'rgba(184,100,10,0.22)' : '#fbe7d6',
              padding: '2px 8px', borderRadius: 12 }}>Requires submittals</span>
        )}
      </div>

      <div style={lbl}>When it appears</div>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: textPrim, padding: '8px 10px', background: inputBg, borderRadius: 6, border: `0.5px solid ${border}` }}>
        {ruleToEnglish(section.visibility_rule, fieldsByKey)}
      </div>

      {section.outline_summary && <>
        <div style={lbl}>Outline summary (Selections Package release)</div>
        <div style={bodyText}><TemplateView nodes={parseTemplateNodes(section.outline_summary)} onVar={openVar} theme={vtheme} /></div>
      </>}
      {section.client_action_template && <>
        <div style={lbl}>Client action (owner decision)</div>
        <div style={bodyText}><TemplateView nodes={parseTemplateNodes(section.client_action_template)} onVar={openVar} theme={vtheme} /></div>
      </>}

      <div style={lbl}>Full spec body — outline {Array.isArray(lines) && lines.length ? '(' + lines.length + ' lines)' : ''}</div>
      {lines == null ? (
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: textMut, fontStyle: 'italic' }}>Loading outline…</div>
      ) : lines.length ? (
        <OutlineLines lines={lines} section={section} theme={vtheme} onVar={openVar} doCopy={doCopy} copied={copied} />
      ) : (
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.55, color: textPrim }}>
          <TemplateView nodes={parseTemplateNodes(section.body_template || '')} onVar={openVar} theme={vtheme} />
        </div>
      )}

      <div style={lbl}>Variables used here ({usedKeys.length})</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {usedKeys.length === 0 && <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: textMut, fontStyle: 'italic' }}>None — static text.</span>}
        {usedKeys.map(k => {
          const d = describeVariable(k, fieldsByKey)
          return (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: textPrim }}>
              <span style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#fff', background: SRC_COLOR[d.source], borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>{d.source}</span>
              <span style={{ flex: 1 }}>{d.label}</span>
              <button style={refChip} title="Copy the variable key" onClick={() => doCopy(d.key, 'k' + k)}>{copied === ('k' + k) ? '✓' : d.key}</button>
            </div>
          )
        })}
      </div>

      <div style={{ marginTop: 18, padding: '10px 12px', borderRadius: 6, border: `0.5px dashed ${border}`, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: textMut }}>
        To change this section, copy <button style={refChip} onClick={() => doCopy(ref, 'ref2')}>{copied === 'ref2' ? '✓ copied' : ref}</button> and tell Claude Code what to change (e.g. "in {section.section_number}, only show the leak-detection clause when …"). Edits happen on the back end — faster and safer than editing here.
      </div>

      {popover && <VarPopover popover={popover} theme={theme} onCopy={k => doCopy(k, 'pop')} copied={copied === 'pop'} />}
    </div>
  )
}

/* Renders a parsed template tree: text, highlighted variable chips, and plain-English compare callouts. */
function TemplateView({ nodes, onVar, theme, depth = 0 }) {
  const { border, textMut, textPrim, dark } = theme
  return (
    <span style={{ whiteSpace: 'pre-wrap' }}>
      {nodes.map((n, i) => {
        if (n.type === 'text') return <span key={i}>{n.text}</span>
        if (n.type === 'var') {
          const short = n.key.split('.').pop()
          return <button key={i} onClick={e => onVar(e, n.key)}
            style={{ display: 'inline', font: 'inherit', cursor: 'pointer', background: dark ? 'rgba(255,214,102,0.18)' : '#fdf3d0', color: textPrim,
              border: `0.5px solid ${dark ? 'rgba(255,214,102,0.4)' : '#e8d28a'}`, borderRadius: 3, padding: '0 3px' }}
            title="Click for source & key">{n.md ? '🗒 ' : ''}{short}</button>
        }
        // compare block
        const eng = ruleToEnglish(n.cond, theme.fieldsByKey)
        return (
          <span key={i} style={{ display: 'block', borderLeft: `2px solid ${dark ? 'rgba(138,99,210,0.5)' : '#c9b6ec'}`, paddingLeft: 8, margin: '4px 0' }}>
            <span style={{ display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: '#8a63d2', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Shown when {eng}</span>
            <TemplateView nodes={n.children} onVar={onVar} theme={theme} depth={depth + 1} />
            {n.elseChildren && <>
              <span style={{ display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: textMut, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 3 }}>Otherwise</span>
              <TemplateView nodes={n.elseChildren} onVar={onVar} theme={theme} depth={depth + 1} />
            </>}
          </span>
        )
      })}
    </span>
  )
}

/* Renders a section's master lines as a numbered, indented outline (the same legal-outline numbering
 * the editor uses). Lines that share a visibility condition are grouped into a colour-coded band so the
 * relationship between answers and what appears is visible at a glance — same colour = same condition.
 * A legend up top maps each colour to its plain-English condition; each line keeps a copyable
 * spec-line:<id> reference for pointing Claude Code at it. */
function OutlineLines({ lines, section, theme, onVar, doCopy, copied }) {
  const { textMut, textPrim, dark, border } = theme
  const labels = useMemo(() => computeOutlineLabels(lines), [lines])

  // Assign each DISTINCT condition a stable colour + index (first-appearance order), and tally usage.
  const condMeta = useMemo(() => {
    const order = [], colorBySig = {}, ruleBySig = {}, countBySig = {}
    for (const l of lines) {
      const sig = ruleSignature(l.visibility_rule)
      if (!sig) continue
      if (!(sig in colorBySig)) { colorBySig[sig] = COND_PALETTE[order.length % COND_PALETTE.length]; ruleBySig[sig] = l.visibility_rule; order.push(sig) }
      countBySig[sig] = (countBySig[sig] || 0) + 1
    }
    return { order, colorBySig, ruleBySig, countBySig }
  }, [lines])

  // Group consecutive lines by condition signature (a run = lines that appear/disappear together).
  const groups = useMemo(() => {
    const out = []
    for (let i = 0; i < lines.length; i++) {
      const sig = ruleSignature(lines[i].visibility_rule)
      const prev = out[out.length - 1]
      if (prev && prev.sig === sig) prev.lines.push(lines[i])
      else out.push({ sig, rule: lines[i].visibility_rule, lines: [lines[i]] })
    }
    return out
  }, [lines])

  const chip = { fontFamily: '"DM Mono",monospace', fontSize: 9, background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(7,39,45,0.06)', color: textMut, border: 'none', borderRadius: 3, padding: '1px 5px', cursor: 'pointer', flexShrink: 0 }
  const lnIndex = new Map(lines.map((l, i) => [l.id, i + 1]))

  function renderLine(l) {
    const isBlock = l.kind === 'block'
    const indent = isBlock ? 0 : (l.level || 0) * 22
    const label = labels.get(l.id) || ''
    const tpl = isBlock ? ((l.block_payload && l.block_payload.raw_template) || '') : (l.text_template || '')
    const ref = 'spec-line:' + l.id
    const ln = 'L' + lnIndex.get(l.id)
    return (
      <div key={l.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '2px 0', marginLeft: indent }}>
        <button style={chip} title={'Copy reference: ' + ref} onClick={() => doCopy(ref, 'ln' + l.id)}>{copied === ('ln' + l.id) ? '✓' : ln}</button>
        {label
          ? <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11.5, color: textMut, minWidth: 22, flexShrink: 0, paddingTop: 1 }}>{label}</span>
          : <span style={{ minWidth: isBlock ? 0 : 22, flexShrink: 0 }} />}
        <div style={{ flex: 1, minWidth: 0, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.5, color: textPrim, fontWeight: l.kind === 'heading' ? 600 : 400 }}>
          {isBlock && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 8.5, color: textMut, textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 6 }}>raw block</span>}
          <TemplateView nodes={parseTemplateNodes(tpl)} onVar={onVar} theme={theme} />
        </div>
      </div>
    )
  }

  return (
    <div style={{ marginTop: 2 }}>
      {condMeta.order.length > 0 && (
        <ConditionLegend condMeta={condMeta} theme={theme} />
      )}
      {groups.map((g, gi) => {
        if (!g.sig) {
          // Always-shown lines: no band, a faint "always" marker so they read as unconditional.
          return (
            <div key={gi} style={{ marginLeft: 4, paddingLeft: 9, borderLeft: `2px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(7,39,45,0.10)'}`, margin: '2px 0' }}>
              {g.lines.map(renderLine)}
            </div>
          )
        }
        const color = condMeta.colorBySig[g.sig]
        const idx = condMeta.order.indexOf(g.sig) + 1
        return (
          <div key={gi} style={{ marginLeft: 4, paddingLeft: 9, borderLeft: `2.5px solid ${color}`, margin: '6px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <span style={{ width: 13, height: 13, borderRadius: 3, background: color, color: '#fff', fontFamily: '"DM Mono",monospace', fontSize: 8, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{idx}</span>
              <ConditionChips rule={g.rule} color={color} theme={theme} />
            </div>
            {g.lines.map(renderLine)}
          </div>
        )
      })}
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: textMut, marginTop: 10, fontStyle: 'italic' }}>
        Coloured bands group lines that appear together; same colour = same condition (see key above). Click an L-chip to copy a line's reference and paste it to Claude Code.
      </div>
    </div>
  )
}

/* The section's condition key: each colour → its plain-English rule + how many lines it gates. */
function ConditionLegend({ condMeta, theme }) {
  const { textMut, textPrim, dark, border } = theme
  return (
    <div style={{ border: `0.5px solid ${border}`, borderRadius: 6, padding: '8px 10px', marginBottom: 10, background: dark ? 'rgba(255,255,255,0.02)' : '#fff' }}>
      <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 8.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: textMut, marginBottom: 6 }}>How this section is gated</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {condMeta.order.map((sig, i) => {
          const color = condMeta.colorBySig[sig]
          return (
            <div key={sig} style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
              <span style={{ width: 13, height: 13, borderRadius: 3, background: color, color: '#fff', fontFamily: '"DM Mono",monospace', fontSize: 8, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
              <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, lineHeight: 1.4, color: textPrim }}>
                {ruleToEnglish(condMeta.ruleBySig[sig], theme.fieldsByKey)}
              </span>
              <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: textMut, flexShrink: 0, marginTop: 2 }}>{condMeta.countBySig[sig]} {condMeta.countBySig[sig] === 1 ? 'line' : 'lines'}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* A condition rendered as field pills joined by AND / OR — shown once at the top of each band. */
function ConditionChips({ rule, color, theme }) {
  const { dark, textMut } = theme
  const { join, parts } = ruleToParts(rule, theme.fieldsByKey)
  const pill = { fontFamily: '"DM Sans",sans-serif', fontSize: 10, lineHeight: 1.3, color: dark ? '#cfe2e8' : '#07272D',
    background: color + (dark ? '33' : '1f'), border: `0.5px solid ${color}`, borderRadius: 10, padding: '1px 7px' }
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', minWidth: 0 }}>
      {parts.map((p, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {i > 0 && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 8.5, fontWeight: 700, letterSpacing: '0.05em', color: textMut }}>{join}</span>}
          <span style={pill}>{p.text}</span>
        </span>
      ))}
    </span>
  )
}

function VarPopover({ popover, theme, onCopy, copied }) {
  const { dark } = theme
  const { v, top, left } = popover
  return (
    <div onClick={e => e.stopPropagation()} style={{ position: 'fixed', top, left, width: 300, zIndex: 20,
      background: dark ? '#0d1e24' : '#fff', border: `0.5px solid ${dark ? 'rgba(255,255,255,0.18)' : 'rgba(7,39,45,0.18)'}`,
      borderRadius: 8, boxShadow: '0 8px 28px rgba(7,39,45,0.22)', padding: 12, fontFamily: '"DM Sans",sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#fff', background: SRC_COLOR[v.source], borderRadius: 4, padding: '1px 6px' }}>{v.source}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: dark ? '#cfe2e8' : '#07272D' }}>{v.label}</span>
      </div>
      <div style={{ fontSize: 11.5, color: dark ? 'rgba(207,226,232,0.7)' : 'rgba(7,39,45,0.65)', lineHeight: 1.5 }}>{v.detail}</div>
      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <code style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: dark ? '#cfe2e8' : '#07272D' }}>{v.key}</code>
        <button onClick={() => onCopy(v.key)} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, background: 'none', border: `0.5px solid ${dark ? 'rgba(255,255,255,0.18)' : 'rgba(7,39,45,0.18)'}`, borderRadius: 4, color: dark ? '#cfe2e8' : '#07272D', cursor: 'pointer', padding: '2px 7px' }}>{copied ? '✓ copied' : 'copy key'}</button>
      </div>
    </div>
  )
}

/* ── Questionnaire tab — read-only reverse map ─────────────────────────────── */
function QuestionnaireTab({ sections, fields, theme }) {
  const { border, textPrim, textMut, dark } = theme
  // field key → [section_number] that reference it
  const drives = useMemo(() => {
    const m = {}
    for (const s of sections) for (const k of sectionReferencedKeys(s)) (m[k] = m[k] || []).push(s.section_number)
    return m
  }, [sections])
  const byDiv = {}
  for (const f of fields) { const d = f.division || 'general'; (byDiv[d] = byDiv[d] || []).push(f) }
  const divs = Object.keys(byDiv).sort()
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: textMut, marginBottom: 12 }}>Each question, its inputs, and the sections it drives. Read-only — to change a question, ask Claude Code with its key.</div>
      {divs.map(d => (
        <div key={d} style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: textMut, marginBottom: 6 }}>Division {d}</div>
          {byDiv[d].map(f => (
            <div key={f.id || f.key} style={{ padding: '7px 10px', marginBottom: 5, borderRadius: 6, border: `0.5px solid ${border}`, background: dark ? 'rgba(255,255,255,0.02)' : '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <code style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: '#1D9E75' }}>{f.key}</code>
                <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: textPrim }}>{f.label}</span>
                <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: textMut }}>{f.type}</span>
              </div>
              {Array.isArray(f.options) && f.options.length > 0 && (
                <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: textMut, marginTop: 3 }}>options: {f.options.join(' · ')}</div>
              )}
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: textMut, marginTop: 3 }}>
                drives: {(drives[f.key] && drives[f.key].length) ? drives[f.key].join(', ') : '— (no section references this yet)'}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
